import { UnryoNode, UnryoRole, NodeStatus } from '@/model'
import { Group } from './network'
import { UnryoIssue } from '@/model/node'

type TopoGroup = Group | 'unknown'

/**
 * Models a node used in the topology.
 */
export interface TopologyNode {
  id: string
  label: string
  group: TopoGroup
  level: number
  namespace: string
  roles: UnryoRole[]
  timestamp: number
  metadata: {
    externalIp: string | undefined
  },
  health: {
    status: NodeStatus,
    availabilityIssue?: UnryoIssue,
    performanceIssue?: UnryoIssue
  }
}

/**
 * Models an edge used in the topology.
 */
export interface TopologyEdge {
  label: string
  from: string
  to: string
}

/**
 * Map relation -> name -> protocol -> role-ids
 */
export interface RoleMap {
  [s: string]: {
    [s: string]: {
      [s: string]: string[]
    }
  }
}

/**
 * Holds the columns within which the elements will be added.
 */
const LEVELS: Record<TopoGroup, number> = {
  collector: 0,
  database: 2,
  vault: 2,
  analytic: 3,
  report: 4,
  portal: 5,
  unknown: 6
}

/**
 * Finds the connection matching role.
 *
 * @param role The role for which to find the matching one.
 * @returns The role that matches the provided one.
 */
function target (role: string) {
  switch (role) {
    case 'producer':
      return 'receiver'
    case 'consumer':
      return 'supplier'
  }
}

function addNode (nodes: TopologyNode[], node: UnryoNode) {
  const grp = node.group || 'unknown'
  nodes.push({
    id: node.id,
    label: node.label,
    group: grp,
    level: LEVELS[grp],
    namespace: node.namespace,
    roles: node.roles,
    timestamp: node.timestamp,
    metadata: {
      externalIp: node.metadata.externalIp
    },
    health: {
      status: node.health.status,
      availabilityIssue: node.health.availabilityIssue,
      performanceIssue: node.health.performanceIssue
    }
  })
}

function mapRoles (nodes: UnryoNode[]) {
  const map: RoleMap = {}
  nodes.forEach((node) => {
    node.roles.forEach((role) => {
      const relation = role.relation
      const name = role.role
      const protocol = role.protocol
      if (!map[relation]) {
        map[relation] = {}
      }
      if (!map[relation][name]) {
        map[relation][name] = {}
      }
      if (!map[relation][name][protocol]) {
        map[relation][name][protocol] = []
      }
      map[relation][name][protocol].push(node.id)
    })
  })
  return map
}

function addEdgesFrom (edges: TopologyEdge[], roles: RoleMap, node: UnryoNode) {
  node.roles.forEach((role) => {
    const to = target(role.role)
    if (to) {
      const relation = role.relation
      const protocol = role.protocol
      if (roles[relation] && roles[relation][to] && roles[relation][to][protocol]) {
        roles[relation][to][protocol].forEach((id) => {
          edges.push({
            from: node.id,
            to: id,
            label: relation
          })
        })
      }
    }
  })
}

export function build (data: UnryoNode[]) {
  const nodes: TopologyNode[] = []
  const edges: TopologyEdge[] = []

  const roles = mapRoles(data)

  data.forEach((node) => {
    addNode(nodes, node)
    addEdgesFrom(edges, roles, node)
  })

  return { nodes, edges }
}
